/**
 * Wire types for the image moderation queue. Mirrors
 * api/controllers/moderation/images-to-moderate.js and image-scan-history.js.
 */

export type ImageScanVerdict =
  | 'pending'
  | 'clear'
  | 'questionable'
  | 'scan_failed'
  | 'auto_hidden'
  | 'skipped';

export type TimestampValue = {
  displayValue: string;
  value: string;
};

export type MatchedCategory = {
  key: string;
  confidence: number;
  reason: string;
  /** Caused the hide, rather than merely being flagged. */
  isHideReason: boolean;
};

export type CategoryScore = {
  confidence: number | null;
  reason: string;
};

export type ModeratedImage = {
  uuid: string;
  image: string;
  scanVerdict: ImageScanVerdict;
  hidden: boolean;
  hiddenBy: string | null;
  hiddenAt: TimestampValue | null;
  createdAt: TimestampValue;
  /**
   * 'listing' = a Marketplace v2 listing photo; parentContent is its title.
   * 'listingComment' = a photo on a listing comment; parentContent is the
   * comment text, or the listing title for a photo-only comment.
   */
  parentType:
    | 'groupComment'
    | 'groupEventComment'
    | 'listing'
    | 'listingComment';
  parentUuid: string;
  parentContent: string | null;
  authorName: string;
  buildingId: string | null;
  buildingName: string | null;
  groupName: string | null;
  groupId: string | null;
  categories: Record<string, CategoryScore>;
  matchedCategories: MatchedCategory[];
  hideCategories: string[];
  reviewCategories: string[];
  summary: string | null;
  provider: string | null;
  scanError: { code?: string; message?: string } | null;
};

export type Thresholds = {
  autoHide: number;
  questionable: number;
};

export type QueueBuilding = {
  buildingId: string;
  buildingName: string;
};

export type ImageQueueResponse = {
  images: ModeratedImage[];
  page: number;
  pageSize: number;
  /** Rows matching the current filters, not just this page. */
  total: number;
  totalPages: number;
  /** Per-tab totals for the badges; ignores the category filter. */
  counts: { hidden: number; review: number };
  buildings: QueueBuilding[];
  thresholds: Thresholds;
};

export type ImageScanRow = {
  uuid: string;
  provider: string;
  model: string;
  state: ImageScanVerdict | null;
  categories: Record<string, CategoryScore>;
  hideCategories: string[];
  reviewCategories: string[];
  summary: string | null;
  attempt: number;
  error: { code?: string; message?: string } | null;
  promptVersion: string | null;
  latencyMs: number | null;
  createdAt: string;
  /** Whose verdict actually decided this photo. The rest are comparison only. */
  isPrimary: boolean;
};

export type ImageScanHistoryResponse = {
  image: {
    uuid: string;
    image: string;
    scanVerdict: ImageScanVerdict;
    hidden: boolean;
    hiddenBy: string | null;
    hiddenByName: string | null;
    hiddenAt: string | null;
    moderatedBy: string | null;
    moderatedByName: string | null;
    moderatedAt: string | null;
  };
  scans: ImageScanRow[];
  thresholds: Thresholds;
  neverAutoHide: string[];
};

// Keys must match CATEGORY_KEYS in api/constants/imageModerationPolicy.js, or
// the table falls back to showing the raw key.
export const CATEGORY_LABELS: Record<string, string> = {
  standard_violations: 'Standard violations',
  dog_waste: 'Dog waste',
  unit_identifying_info: 'Unit-identifying info',
  people: 'People',
  pii: 'PII',
  license_plates: 'License plates',
  dirty_broken: 'Dirty / broken'
};

export const categoryLabel = (key: string) => CATEGORY_LABELS[key] ?? key;

/** Flagged for review at 50-100%, but never auto-hidden. */
export const PEOPLE_CATEGORY = 'people';
export const ALL_CATEGORIES = 'all';
